import { useEffect, useRef, type ReactNode } from 'react';
import {
  ArrowLeft,
  CreditCard,
  Gift,
  Loader2,
  MessageCircle,
  Server,
  WalletCards,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';

export type SettingsSection =
  | 'billing-credits'
  | 'billing-plans'
  | 'billing-redeem'
  | 'computers'
  | 'discord'
  | 'slack'
  | 'wechat'
  | 'telegram'
  | 'feishu'
  | 'legacy-connect';

type SettingsLayoutProps = {
  current: SettingsSection;
  notice?: string;
  error?: string;
  onNoticeDismiss?: () => void;
  onErrorDismiss?: () => void;
  children: ReactNode;
};

type SettingsNavItem = {
  id: SettingsSection;
  label: string;
  href: string;
  icon: ReactNode;
  tone?: 'primary' | 'secondary';
};

const backItem = {
  label: 'Back to chat',
  href: '/chat',
  icon: <ArrowLeft className="size-4" />,
};

const navGroups: Array<{ label: string; items: SettingsNavItem[] }> = [
  {
    label: 'Account',
    items: [
      {
        id: 'billing-credits',
        label: 'Credits',
        href: '/settings/billing/credits',
        icon: <WalletCards className="size-4" />,
      },
      {
        id: 'billing-plans',
        label: 'Plans',
        href: '/settings/billing/plans',
        icon: <CreditCard className="size-4" />,
      },
      {
        id: 'billing-redeem',
        label: 'Redeem code',
        href: '/settings/billing/redeem',
        icon: <Gift className="size-4" />,
      },
    ],
  },
  {
    label: 'Devices',
    items: [
      {
        id: 'computers',
        label: 'Connected computers',
        href: '/settings/computers',
        icon: <Server className="size-4" />,
      },
    ],
  },
  {
    label: 'Integrations',
    items: [
      {
        id: 'discord',
        label: 'Discord',
        href: '/settings/integrations/discord',
        icon: <MessageCircle className="size-4 text-[#5865F2]" />,
      },
      {
        id: 'telegram',
        label: 'Telegram',
        href: '/settings/integrations/telegram',
        icon: <MessageCircle className="size-4 text-[#229ED9]" />,
      },
    ],
  },
];

export function SettingsPageLoading({ label }: { label: string }) {
  return (
    <div className="flex h-[100dvh] items-center justify-center bg-background text-sm text-muted-foreground">
      <Loader2 className="mr-2 size-4 animate-spin" />
      {label}
    </div>
  );
}

export function SettingsContentLoading({ label }: { label: string }) {
  return (
    <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-dashed border-border/80 bg-muted/20 text-sm text-muted-foreground">
      <Loader2 className="mr-2 size-4 animate-spin" />
      {label}
    </div>
  );
}

export function SettingsLayout({
  current,
  notice,
  error,
  onNoticeDismiss,
  onErrorDismiss,
  children,
}: SettingsLayoutProps) {
  const onNoticeDismissRef = useRef(onNoticeDismiss);
  const onErrorDismissRef = useRef(onErrorDismiss);

  useEffect(() => {
    onNoticeDismissRef.current = onNoticeDismiss;
  }, [onNoticeDismiss]);

  useEffect(() => {
    onErrorDismissRef.current = onErrorDismiss;
  }, [onErrorDismiss]);

  useEffect(() => {
    if (!error) {
      return;
    }

    toast.error(error, {
      duration: 5000,
      onAutoClose: () => onErrorDismissRef.current?.(),
      onDismiss: () => onErrorDismissRef.current?.(),
    });
  }, [error]);

  useEffect(() => {
    if (!notice || error) {
      return;
    }

    toast.success(notice, {
      duration: 3500,
      onAutoClose: () => onNoticeDismissRef.current?.(),
      onDismiss: () => onNoticeDismissRef.current?.(),
    });
  }, [error, notice]);

  return (
    <div
      data-testid="settings-shell"
      data-settings-section={current}
      className="flex h-[100dvh] flex-col overflow-hidden bg-background text-foreground md:flex-row"
    >
      <aside className="flex shrink-0 flex-col border-b border-border/70 bg-muted/20 md:w-[260px] md:border-b-0 md:border-r">
        <nav className="min-w-0 overflow-x-auto px-2 py-2 md:overflow-y-auto md:px-3 md:py-4">
          <div className="flex gap-2 md:flex-col md:gap-5">
            <Link
              to={backItem.href}
              aria-label={backItem.label}
              className="inline-flex h-9 min-w-max items-center gap-2 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:w-full md:min-w-0"
            >
              {backItem.icon}
              <span>{backItem.label}</span>
            </Link>

            {navGroups.map(group => (
              <div key={group.label} className="min-w-max md:min-w-0">
                <div className="hidden px-2 pb-1 text-xs font-medium text-muted-foreground md:block">
                  {group.label}
                </div>
                <div className="flex gap-1 md:flex-col">
                  {group.items.map(item => {
                    const active = item.id === current;
                    const secondary = item.tone === 'secondary';
                    return (
                      <Link
                        key={item.id}
                        to={item.href}
                        className={cn(
                          'inline-flex items-center gap-2 rounded-md transition-colors md:w-full',
                          secondary
                            ? 'h-8 px-2.5 text-xs'
                            : 'h-9 px-2.5 text-sm',
                          active
                            ? secondary
                              ? 'bg-muted text-foreground/80 ring-1 ring-border/60'
                              : 'bg-background text-foreground shadow-sm ring-1 ring-border'
                            : secondary
                              ? 'text-muted-foreground/70 hover:bg-muted hover:text-foreground/80'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </nav>
      </aside>

      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <main className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6">
          <div className="mx-auto w-full max-w-5xl">{children}</div>
        </main>
      </section>
    </div>
  );
}
